import { useEffect, useRef, useState } from 'react';
import PageLayout from '../../components/feature/PageLayout';
import PageHero from '../../components/feature/PageHero';
import SectionHeading from '../../components/base/SectionHeading';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { attachTilt } from '../../hooks/useTiltCards';
import { portfolioCompanies, sectors } from '../../mocks/portfolio';

function ThesisSection() {
  const revealRef = useScrollReveal<HTMLDivElement>({ stagger: 0.1 });
  const tiltRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!tiltRef.current) return;
    return attachTilt(tiltRef.current, 4);
  }, []);

  const items = [
    {
      n: '01',
      title: 'Compute is the new oil',
      body: 'The next decade of intelligence will be defined by who controls inference. We back AI infrastructure end-to-end — silicon to model to deployment.',
    },
    {
      n: '02',
      title: 'Biology becomes engineering',
      body: 'Programmable cells, ML-designed therapeutics, and automated wetlabs are turning biology into a software discipline.',
    },
    {
      n: '03',
      title: 'Atoms catch up to bits',
      body: 'Robotics, manufacturing and energy are entering a software-defined era. We back the founders rewiring the physical world.',
    },
  ];

  return (
    <section ref={revealRef} className="relative bg-[#070709] py-24 md:py-32 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <SectionHeading
          eyebrow="Thesis"
          title="Three frontiers we believe in."
          className="mb-16"
        />
        <div ref={tiltRef} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((it) => (
            <div
              key={it.n}
              className="gsap-reveal tilt-card hover-glow relative glass-card rounded-2xl p-8 min-h-[260px] flex flex-col justify-between"
            >
              <div className="font-mono text-xs uppercase tracking-[0.25em] text-white/30">{it.n}</div>
              <div>
                <h3 className="font-display text-2xl md:text-3xl mb-3">
                  <span className="title-shimmer inline-block">{it.title}</span>
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">{it.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SectorsSection() {
  const revealRef = useScrollReveal<HTMLDivElement>({ stagger: 0.08 });
  const tiltRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!tiltRef.current) return;
    return attachTilt(tiltRef.current, 3);
  }, []);

  return (
    <section ref={revealRef} className="relative bg-[#050505] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <SectionHeading
          eyebrow="Sectors"
          title="Where we deploy capital."
          description="Our portfolio spans six core sectors. We invest with sector-specialist partners who have built and operated in each domain."
          className="mb-16"
        />
        <div ref={tiltRef} className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/5">
          {sectors.map((s, i) => (
            <div
              key={s.name}
              className="gsap-reveal tilt-card hover-glow group bg-[#0a0a0c] p-7 md:p-9 hover:bg-[#0e0e12] transition-colors flex flex-col gap-5"
            >
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl liquid-glass tilt-lift">
                  <i className={`${s.icon} text-xl`}></i>
                </div>
                <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/30">
                  0{i + 1}
                </span>
              </div>
              <div>
                <h3 className="font-display text-xl md:text-2xl">
                  <span className="title-shimmer inline-block">{s.name}</span>
                </h3>
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/40 mt-2">
                  {s.count} companies
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PortfolioGrid() {
  const allStages = ['All', 'Pre-seed', 'Seed', 'Series A'];
  const [filter, setFilter] = useState('All');
  const revealRef = useScrollReveal<HTMLDivElement>({ stagger: 0.08 });
  const tiltRef = useRef<HTMLDivElement | null>(null);

  const filtered =
    filter === 'All'
      ? portfolioCompanies
      : portfolioCompanies.filter((c) => c.stage === filter);

  useEffect(() => {
    if (!tiltRef.current) return;
    return attachTilt(tiltRef.current, 4);
  }, [filter]);

  return (
    <section
      ref={revealRef}
      data-product-shop
      className="relative bg-[#070709] py-24 md:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12">
          <SectionHeading
            eyebrow="Portfolio · all"
            title="82 companies. One conviction."
          />
          <div className="gsap-reveal flex flex-wrap gap-1 liquid-glass p-1 rounded-full">
            {allStages.map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest transition-colors whitespace-nowrap cursor-pointer ${
                  filter === s ? 'bg-white text-black' : 'text-white/70 hover:text-white'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div
          ref={tiltRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
        >
          {filtered.map((c) => (
            <article
              key={c.name}
              className="gsap-reveal tilt-card hover-glow group glass-card rounded-2xl overflow-hidden cursor-pointer"
            >
              <div className="relative aspect-[5/4] overflow-hidden">
                <img
                  src={c.image}
                  alt={`${c.name} ${c.sector}`}
                  title={`${c.name} - ${c.sector}`}
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <span className="absolute top-4 left-4 liquid-glass px-3 py-1 rounded-full text-[11px] font-mono uppercase tracking-widest tilt-lift">
                  {c.stage} · {c.year}
                </span>
              </div>
              <div className="p-6">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <h3 className="font-display text-2xl">
                    <span className="title-shimmer inline-block">{c.name}</span>
                  </h3>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-white/40 mt-1.5 whitespace-nowrap">
                    {c.sector}
                  </span>
                </div>
                <p className="text-sm text-white/60 leading-relaxed">{c.description}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProcessSection() {
  const revealRef = useScrollReveal<HTMLDivElement>({ stagger: 0.1 });
  const tiltRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!tiltRef.current) return;
    return attachTilt(tiltRef.current, 4);
  }, []);

  const steps = [
    { n: '01', t: 'First call', d: 'A 30-minute conversation — no decks required. We listen, then ask sharp questions.' },
    { n: '02', t: 'Deep dive', d: 'Technical diligence with sector partners. References, code reviews, customer calls.' },
    { n: '03', t: 'Decision', d: 'A term sheet within 7 days. Wire within 10. We move fast when conviction lands.' },
    { n: '04', t: 'Onboard', d: 'Hiring, design, GTM, follow-on intros. We move to your tempo, not ours.' },
  ];

  return (
    <section ref={revealRef} className="relative bg-[#050505] py-24 md:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <SectionHeading
          eyebrow="Process"
          title="From first call to wired check."
          description="No theater. No three-month dance. A clear process built around founder time."
          className="mb-16"
        />
        <div ref={tiltRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s) => (
            <div
              key={s.n}
              className="gsap-reveal tilt-card hover-glow glass-card rounded-2xl p-7 min-h-[220px] flex flex-col justify-between"
            >
              <div className="text-xs font-mono uppercase tracking-[0.25em] text-white/30">{s.n}</div>
              <div>
                <h3 className="font-display text-2xl mb-2">
                  <span className="title-shimmer inline-block">{s.t}</span>
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function InvestingPage() {
  return (
    <PageLayout transparentNav>
      <PageHero
        eyebrow="Investing"
        title="Conviction-led capital for technical founders."
        description="We write first checks into ambitious technical founders building infrastructure for the next decade — across AI, robotics, biotech, climate, and space."
        image="https://readdy.ai/api/search-image?query=Sweeping%20abstract%20cinematic%20visualization%20of%20warm%20golden%20light%20rays%20diffusing%20through%20dark%20foggy%20atmosphere%20with%20faint%20floating%20particles%2C%20premium%20editorial%20finance%20magazine%20aesthetic%2C%20minimal%20composition%20with%20deep%20negative%20space&width=1800&height=900&seq=vex-investing-hero&orientation=landscape"
      />
      <ThesisSection />
      <SectorsSection />
      <PortfolioGrid />
      <ProcessSection />
    </PageLayout>
  );
}